import { BulkExportProjects } from "@components/table/projects/bulk-actions/bulk-export-projects";
import { WorldMap } from "@components/world-map/world-map";
import { WorldMapHandle } from "@components/world-map/world-map-types";
import { useErrorContext } from "@context-providers/error-boundary/error-handling-context";
import { BaseCompanyIdProps } from "@custom-types/base-types";
import {
  PaginatedProjects,
  ProjectArchivingState,
} from "@custom-types/project-types";
import { red, XMarkCircleFillIcon } from "@flat-ui";
import { useMediaQueryList } from "@hooks/use-media-query";
import { Box, Stack } from "@mui/material";
import { ProjectMarkerPopupContent } from "@pages/projects/projects-map/project-marker-popup-content";
import { ProjectsMapContainer } from "@pages/projects/projects-map/projects-map-container";
import { ProjectsMapInfoPage } from "@pages/projects/projects-map/projects-map-info-page";
import { ProjectsMapList } from "@pages/projects/projects-map/projects-map-list";
import { ProjectsMapSkeleton } from "@pages/projects/projects-map/projects-map-skeleton";
import { useProjectsMapDeepLink } from "@pages/projects/projects-map/use-projects-map-deep-link";
import {
  mapProjectsByArchivingStateSelector,
  mapProjectsFetchingStatusSelector,
} from "@store/projects-map/projects-map-selectors";
import { fetchMapProjects } from "@store/projects-map/projects-map-thunks";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import {
  selectedEntitiesSelector,
  selectedRowIdsSelector,
} from "@store/table/table-selector";
import { setInitialFetchingItems } from "@store/table/table-slice";
import { searchSelector } from "@store/ui/ui-selector";
import { setContentFullWidth } from "@store/ui/ui-slice";
import { getSvgColoredIconCss } from "@styles/common-styles";
import { MAP_EXPORT_EXCLUDED_COLUMNS } from "@utils/file-data-export";
import { sortProjects } from "@utils/project-utils";
import { matchesAnySearchWord } from "@utils/string-utils";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { WorldMapEvents } from "@utils/track-event/track-event-list";
import { EntitiesToolbar } from "@components/common/toolbar/entities-toolbar";
import { ProjectsSort } from "@components/projects/projects-sort";
import { isSdbProjectWithPosition } from "@custom-types/project-type-guards";

const EMPTY_SET: ReadonlySet<string> = new Set();

interface Props
  extends BaseCompanyIdProps, Pick<PaginatedProjects, "sortBy" | "setSortBy"> {
  /** The currently selected project archiving state */
  projectArchivingState: ProjectArchivingState;
}

/** World map that displays projects */
export function ProjectsMap({
  companyId,
  projectArchivingState,
  sortBy,
  setSortBy,
}: Props): JSX.Element {
  const dispatch = useAppDispatch();
  const { isScreenLgOrLarger } = useMediaQueryList();
  const { handleErrorWithToast } = useErrorContext();
  const { trackEvent } = useTrackEvent();
  const mapRef = useRef<WorldMapHandle>(null);

  const { initialView, onViewStable } = useProjectsMapDeepLink();

  const [viewportProjectIds, setViewportProjectIds] =
    useState<ReadonlySet<string> | null>(null);

  /** IDs of the projects whose sidebar cards should show the highlighted style */
  const [highlightedIds, setHighlightedIds] =
    useState<ReadonlySet<string>>(EMPTY_SET);

  const fetchingStatus = useAppSelector(mapProjectsFetchingStatusSelector);
  const selectedRowIds = useAppSelector(selectedRowIdsSelector);
  const selectedMapProjects = useAppSelector(
    selectedEntitiesSelector("mapProjects")
  );
  const isAnyRowSelected = selectedMapProjects.length !== 0;

  // mapProjectsByArchivingStateSelector is a selector factory.
  // Without memoizing it, each render creates a new createSelector instance,
  // which returns a new projects array reference and can cause the map to reset its viewport.
  const mapProjectsSelector = useMemo(
    () => mapProjectsByArchivingStateSelector(projectArchivingState),
    [projectArchivingState]
  );

  const allProjects = useAppSelector(mapProjectsSelector);

  // Since all projects are fetched upfront, filtering is done client-side and
  // there is no minimum character requirement like the backend-driven list view.
  const { debouncedSearchText } = useAppSelector(searchSelector);
  const search = debouncedSearchText.trim() || undefined;
  const hasSearchQuery = search !== undefined;
  const isLoading =
    fetchingStatus === "uninitialized" || fetchingStatus === "pending";

  /**
   * All available projects filtered based on the following criteria:
   * - Search query: project name must match any of the search words.
   * More may be added here in the future.
   * Sorting is not applied at this stage because it is only relevant for the list and
   * because recreating the project that populate the map causes as short flicker in the map markers, thus
   * by sorting only in the list we avoid the flicker if the user only changes the sorting.
   */
  const filteredProjects = useMemo(() => {
    if (!search) {
      return allProjects;
    }
    return allProjects.filter((p) =>
      matchesAnySearchWord(p.name, search ?? "")
    );
  }, [allProjects, search]);

  /**
   * Filtered projects that have GPS position and can be rendered as markers on the map.
   */
  const filteredProjectsWithPosition = useMemo(() => {
    return filteredProjects.filter(isSdbProjectWithPosition);
  }, [filteredProjects]);

  /**
   * Filtered projects currently inside the map viewport, sorted by the active sort criteria.
   * These are the projects shown in the "Visible on map" section of the list.
   * Falls back to all projects with GPS position before the map reports its first viewport.
   */
  const visibleProjects = useMemo(() => {
    const inViewport = viewportProjectIds
      ? filteredProjectsWithPosition.filter((p) => viewportProjectIds.has(p.id))
      : filteredProjectsWithPosition;

    return sortProjects(inViewport, sortBy);
  }, [filteredProjectsWithPosition, viewportProjectIds, sortBy]);

  /**
   * All remaining filtered projects that are not currently visible in the map viewport,
   * sorted by the active sort criteria. This includes both projects with a GPS position that
   * fall outside the current viewport and projects without any GPS position (which can never
   * be shown as map markers). These are shown in the "Other projects" section of the list.
   */
  const otherProjects = useMemo(() => {
    const visibleIds = new Set(visibleProjects.map((p) => p.id));
    const remaining = filteredProjects.filter((p) => !visibleIds.has(p.id));

    return sortProjects(remaining, sortBy);
  }, [filteredProjects, visibleProjects, sortBy]);

  /**
   * Combined list of all projects shown across both sections.
   * Used for bulk selection syncing, exporting and the toolbar row counts.
   */
  const projectsMapList = useMemo(
    () => [...visibleProjects, ...otherProjects],
    [visibleProjects, otherProjects]
  );

  const handleClusterClick = useCallback(
    (clusterCount: number): void => {
      trackEvent({
        name: WorldMapEvents.expandCluster,
        props: { clusterCount },
      });
    },
    [trackEvent]
  );

  /** Highlights the sidebar card when a single marker is hovered on the map */
  const onMarkerHover = useCallback((entityId: string | null) => {
    setHighlightedIds(entityId ? new Set([entityId]) : EMPTY_SET);
    mapRef.current?.highlightEntity(entityId);
  }, []);

  /** Highlights sidebar cards for all members when a cluster is hovered on the map */
  const onClusterHover = useCallback((memberIds: readonly string[]) => {
    setHighlightedIds(memberIds.length > 0 ? new Set(memberIds) : EMPTY_SET);
  }, []);

  // Keep the same WorldMap element instance between list-selection updates.
  // Without this, dragging the map can cause it to snap back to the previous viewport on release.
  /** Set of projects map list IDs for fast lookup in bulk selection sync. */
  const projectsMapListIds = useMemo(
    () => new Set(projectsMapList.map((p) => p.id)),
    [projectsMapList]
  );

  /**
   *  Stable WorldMap element
   *  Recreating it on selection changes causes the map to snap back to the previous viewport.
   */
  const worldMapElement = useMemo(() => {
    return (
      <WorldMap
        entities={filteredProjectsWithPosition}
        shouldShowMapControls
        onVisibleEntitiesChange={(entities) =>
          setViewportProjectIds(new Set(entities.map((e) => e.id)))
        }
        mapRef={mapRef}
        initialView={initialView}
        onViewStable={onViewStable}
        onEntityHover={onMarkerHover}
        onClusterHover={onClusterHover}
        renderPopupContent={(project) => (
          <ProjectMarkerPopupContent project={project} />
        )}
        onClusterClick={handleClusterClick}
      />
    );
  }, [
    filteredProjectsWithPosition,
    initialView,
    onViewStable,
    onMarkerHover,
    onClusterHover,
    handleClusterClick,
  ]);

  /**
   * Stable callback wrapping mapRef.current?.goTo to avoid passing a stale ref value
   * directly as a prop, which would be undefined if the map view is not yet ready.
   * Clears any active highlight since the map is navigating to the clicked project.
   */
  const goTo = useCallback<WorldMapHandle["goTo"]>((position, zoom) => {
    mapRef.current?.highlightEntity(null);
    mapRef.current?.goTo(position, zoom);
  }, []);

  /** Highlights the map marker for the hovered project card */
  const onHoverChange = useCallback<WorldMapHandle["highlightEntity"]>(
    (entityId) => {
      mapRef.current?.highlightEntity(entityId);
    },
    []
  );

  const projectsMapListElement = useMemo(
    () => (
      <ProjectsMapList
        visibleProjects={visibleProjects}
        otherProjects={otherProjects}
        companyId={companyId}
        projectArchivingState={projectArchivingState}
        goTo={goTo}
        onHoverChange={onHoverChange}
        highlightedProjectIds={highlightedIds}
        hasSearchQuery={hasSearchQuery}
        sortBy={sortBy}
      />
    ),
    [
      visibleProjects,
      otherProjects,
      companyId,
      projectArchivingState,
      goTo,
      onHoverChange,
      highlightedIds,
      hasSearchQuery,
      sortBy,
    ]
  );

  /** Fetches the map projects */
  const getProjects = useCallback(async (): Promise<void> => {
    try {
      await dispatch(fetchMapProjects({ companyId })).unwrap();
    } catch (error) {
      handleErrorWithToast({
        id: `fetchMapProjects-${Date.now().toString()}`,
        title: "Failed to load the map projects.",
        error,
      });
    }
  }, [companyId, dispatch, handleErrorWithToast]);

  /** Fetch all map projects once on initial load */
  useEffect(() => {
    if (fetchingStatus === "uninitialized") {
      void getProjects();
    }
  }, [fetchingStatus, getProjects]);

  /**
   * Let the page content span the full width of the main area while the map view is mounted,
   * so the map is not constrained by the default centered max-width. Reset on unmount.
   */
  useEffect(() => {
    dispatch(setContentFullWidth(true));
    return () => {
      dispatch(setContentFullWidth(false));
    };
  }, [dispatch]);

  /**
   * Keep the bulk selection aligned with the rows shown in the project list.
   * Projects that leave the viewport or no longer match the search are removed from selection
   * so hidden rows are not still selected.
   */
  useEffect(() => {
    if (selectedRowIds.length === 0) {
      return;
    }

    const nextSelectedRowIds = selectedRowIds.filter((rowId) =>
      projectsMapListIds.has(rowId)
    );

    if (nextSelectedRowIds.length !== selectedRowIds.length) {
      dispatch(setInitialFetchingItems(nextSelectedRowIds));
    }
  }, [dispatch, projectsMapListIds, selectedRowIds]);

  if (fetchingStatus === "rejected") {
    return (
      <ProjectsMapContainer>
        <ProjectsMapInfoPage
          title="Failed to load the map projects"
          subtitle="Please reload the page and try again."
          source={(props) => (
            <XMarkCircleFillIcon
              {...props}
              sx={{
                ...props.sx,
                ...getSvgColoredIconCss(red[500]),
                width: "35px",
                height: "35px",
              }}
            />
          )}
        />
      </ProjectsMapContainer>
    );
  }

  const isToolbarActionsReady =
    fetchingStatus === "succeeded" &&
    // Wait for the map viewportProjectIds to bet set for the first time
    // to avoid showing the export button before the map has been initialized.
    viewportProjectIds !== null &&
    projectsMapList.length > 0;

  // The list content depends on which markers are inside the map viewport. Until the map reports
  // its first viewport we can't know that, so the list keeps showing a skeleton to avoid flashing
  // all projects before the map settles (e.g. when opened via a lat/lng/zoom deep link).
  const isListReady =
    fetchingStatus === "succeeded" && viewportProjectIds !== null;

  const projectsForExport = isAnyRowSelected
    ? selectedMapProjects
    : projectsMapList;

  const exportComponent = isToolbarActionsReady ? (
    <BulkExportProjects
      companyId={companyId}
      selectedProjects={projectsForExport}
      subjectType="workspace"
      projectStatus={projectArchivingState}
      areEntitiesSelected
      buttonType="standard"
      columnsToExclude={MAP_EXPORT_EXCLUDED_COLUMNS}
      viewMode="map"
    />
  ) : undefined;

  const toolbarElement = (
    <EntitiesToolbar
      toolbarEntityName="Project"
      // Show the filtered count as the primary number. The total ("X of Y") is only shown
      // while a search is active, so with no search it reads e.g. "57 Projects" and while
      // searching it reads e.g. "3 of 57 Projects".
      numberOfRows={filteredProjects.length}
      totalNumberOfRows={hasSearchQuery ? allProjects.length : undefined}
      isLoading={isLoading}
      shouldShowRowsLabel={isToolbarActionsReady}
      shouldStack={isScreenLgOrLarger}
      shouldRemoveTopMargin={isScreenLgOrLarger}
      sortActions={
        isToolbarActionsReady && (
          <ProjectsSort
            key="projects-sort"
            sortBy={sortBy}
            setSortBy={setSortBy}
          />
        )
      }
      exportComponent={exportComponent}
      shouldSkipValidation
    />
  );

  if (isScreenLgOrLarger) {
    return (
      <ProjectsMapContainer>
        <Stack direction="row" height="100%" sx={{ overflow: "hidden" }}>
          {/* Fixed-width project list column with its toolbar pinned on top. */}
          {/* The width keeps the project name and action buttons visible. */}
          <Stack sx={{ width: "450px", flexShrink: 0, height: "100%", pr: 1 }}>
            {toolbarElement}
            <Box sx={{ flex: 1, minHeight: 0 }}>
              {!isListReady && <ProjectsMapSkeleton />}
              {isListReady && projectsMapListElement}
            </Box>
          </Stack>
          {/* Map fills all the remaining space */}
          <Box sx={{ flex: 1, minWidth: 0, height: "100%" }}>
            {isLoading && <ProjectsMapSkeleton />}
            {fetchingStatus === "succeeded" && worldMapElement}
          </Box>
        </Stack>
      </ProjectsMapContainer>
    );
  }

  return (
    <>
      {toolbarElement}

      <ProjectsMapContainer>
        <Stack height="100%" gap={1}>
          <Box height="50%">
            {isLoading && <ProjectsMapSkeleton />}
            {fetchingStatus === "succeeded" && worldMapElement}
          </Box>
          <Box height="50%">
            {!isListReady && <ProjectsMapSkeleton />}
            {isListReady && projectsMapListElement}
          </Box>
        </Stack>
      </ProjectsMapContainer>
    </>
  );
}
